import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { actionCreators, State } from "@App/state";
import { RentalCarEquipment } from "@App/types/ayvens/rentalCarEquipment";
import { SelectField } from "@App/Components/atoms/common/SelectField";
import { Checkbox } from "@App/Components/common/Checkbox";

interface RentalCarCategory {
  value: string;
  label: string;
  equipments: string[];
}

export const RentalCarSelect: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setRentalCarSelection } = bindActionCreators(actionCreators, dispatch);

  const rentalCarCategories = [
    {
      value: "A",
      label: t("molecules.whitelabel.ayvens.rentalCarSelect.categories.options.a"),
      equipments: [RentalCarEquipment.SAT_NAV_DAILY],
    },
    {
      value: "B",
      label: t("molecules.whitelabel.ayvens.rentalCarSelect.categories.options.b"),
      equipments: [RentalCarEquipment.AUTOMATIC, RentalCarEquipment.SAT_NAV_DAILY],
    },
    {
      value: "I",
      label: t("molecules.whitelabel.ayvens.rentalCarSelect.categories.options.i"),
      equipments: [RentalCarEquipment.AUTOMATIC, RentalCarEquipment.SAT_NAV_DAILY],
    },
  ];

  const rentalCarSelection = useSelector((state: State) => state.orders.rentalCarSelection);
  const [selectedRentalCarCategory, setSelectedRentalCarCategory] = useState<RentalCarCategory>(
    rentalCarSelection
      ? rentalCarCategories.find((option) => option.value === rentalCarSelection.category) ||
          rentalCarCategories[0]
      : rentalCarCategories[0],
  );
  const [selectedEquipments, setSelectedEquipments] = useState<string[]>(
    rentalCarSelection?.equipment ?? [],
  );

  const setRentalCarCategory = (value: string): void => {
    const category =
      rentalCarCategories.find((option) => option.value === value) || rentalCarCategories[0];
    setSelectedRentalCarCategory(category);
    setSelectedEquipments([]);
  };

  const setEquipment = (equipment: string, checked: boolean): void => {
    setSelectedEquipments((prev) =>
      checked ? [...new Set([...prev, equipment])] : prev.filter((e) => e !== equipment),
    );
  };

  useEffect(() => {
    setRentalCarSelection({
      category: selectedRentalCarCategory.value,
      equipment: selectedEquipments,
    });
  }, [selectedRentalCarCategory, selectedEquipments]);

  return (
    <>
      <div className="py-2 my-2">
        <SelectField
          id="rentalCarCategory"
          name="rentalCarCategory"
          value={selectedRentalCarCategory?.value}
          options={rentalCarCategories}
          setValue={setRentalCarCategory}
        />
      </div>
      {selectedRentalCarCategory && (
        <>
          <div className="font-bold mt-5">
            {t("molecules.whitelabel.ayvens.rentalCarSelect.equipments.label")}
          </div>
          <div className="py-2 my-2">
            {selectedRentalCarCategory.equipments.map((equipment) => (
              <Checkbox
                key={equipment}
                status={selectedEquipments.includes(equipment)}
                title={t(
                  `molecules.whitelabel.ayvens.rentalCarSelect.equipments.options.${equipment}`,
                )}
                onChange={(set) => setEquipment(equipment, set)}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};
